// documentIO — сохранение/загрузка документа Кубикон CAD.
//
// Документ хранится как JSON со структурой:
//   {
//     schemaVersion: 2,
//     title: 'string',
//     parameters: [...],
//     features:   [...],   // sketch/extrude/revolve/combine/fillet/chamfer
//     sketches:   [...],   // primitives + planeRef + constraints
//     savedAt:    'ISO date',
//   }
//
// Тела (B-rep shapes из replicad) в JSON НЕ сохраняем — они невоспроизводимы
// между сессиями (разные WASM-указатели). Вместо этого при загрузке заново
// прогоняем все features через rebuildAllBodies → тела восстанавливаются.

import useCadStore from './store/useCadStore';
import { rebuildAllBodies } from './rebuildPipeline';

export const SCHEMA_VERSION = 3;
export const FILE_EXTENSION = 'kubikon.json';
export const FILE_MIME = 'application/json';


// === Serialize ===
// Берём только то, что нужно для воспроизведения документа. bodyMeshes
// и runtime-state (selection, mode, pendingFeature) не сохраняем.
export function serializeDocument() {
    const s = useCadStore.getState();
    const d = s.doc || {};
    return {
        schemaVersion: SCHEMA_VERSION,
        title:      d.title || 'Без названия',
        parameters: d.parameters || [],
        features:   d.features || [],
        sketches:   d.sketches || [],
        workplanes: d.workplanes || [],
        workaxes:   d.workaxes || [],
        // Ниже — справочные поля, при загрузке игнорируются.
        savedAt: new Date().toISOString(),
    };
}


// === Deserialize ===
// Полностью заменяем документ. После замены вызываем rebuildAllBodies
// чтобы тела появились в worker'е и в сцене.
export async function loadDocumentFromJson(data) {
    if (!data || typeof data !== 'object') {
        throw new Error('Невалидный JSON документа');
    }
    if (data.schemaVersion && data.schemaVersion > SCHEMA_VERSION) {
        // Файл из более новой версии — пробуем всё равно открыть, но предупредим
        console.warn(
            `[documentIO] schemaVersion=${data.schemaVersion} новее чем редактор (${SCHEMA_VERSION}). ` +
            'Возможны проблемы.'
        );
    }
    const newDoc = {
        schemaVersion: SCHEMA_VERSION,
        title:      data.title || 'Без названия',
        parameters: Array.isArray(data.parameters) ? data.parameters : [],
        features:   Array.isArray(data.features)   ? data.features   : [],
        bodies:     [],   // metadata; runtime-меши пересоберём
        sketches:   Array.isArray(data.sketches)   ? data.sketches   : [],
        workplanes: Array.isArray(data.workplanes) ? data.workplanes : [],
        workaxes:   Array.isArray(data.workaxes)   ? data.workaxes   : [],
    };
    // Заменяем документ через loadDocument (обнуляет bodyMeshes/visibility/selection)
    useCadStore.getState().loadDocument(newDoc);
    // Прогоняем features заново через worker → новые bodyMeshes
    try {
        await rebuildAllBodies();
    } catch (e) {
        console.error('[documentIO] rebuild after load failed:', e);
        // Документ всё равно загружен — пусть пользователь увидит фичи в timeline
        throw new Error('Документ загружен, но ошибка пересчёта тел: ' + e.message);
    }
}


// === Скачать как файл (Save As) ===
// Браузер триггерит download через blob URL.
export function downloadDocumentFile(filename) {
    const data = serializeDocument();
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: FILE_MIME });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = sanitizeFilename(filename || data.title || 'document') + '.' + FILE_EXTENSION;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Освобождаем URL чуть позже — на некоторых браузерах click() async
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}


// === Открыть файл (Open) ===
// Возвращает Promise<{cancelled} | {data}>. Использует <input type="file">.
export function openDocumentFile() {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = `.${FILE_EXTENSION},.json,application/json`;
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) {
                resolve({ cancelled: true });
                return;
            }
            try {
                const text = await file.text();
                const data = JSON.parse(text);
                resolve({ data });
            } catch (e) {
                reject(new Error('Не удалось прочитать файл: ' + e.message));
            }
        };
        // Если юзер отменил — change не срабатывает; нет надёжного способа
        // обнаружить отмену. Тогда Promise остаётся pending — это норм,
        // компонент перезаписывает его при следующем нажатии «Открыть».
        input.click();
    });
}


// === Sanitize имени файла ===
// Чтобы Windows/Linux/Mac не падали на спецсимволах.
function sanitizeFilename(name) {
    return String(name)
        .replace(/[/\\?%*:|"<>]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 80) || 'document';
}
